<template>
    <authenticated-layout>
        <v-card class="pa-6">
            <v-card-title
                class="d-flex justify-space-between align-center pa-0 pb-2"
            >
                <span>
                    {{ customer.name }} -
                    {{ $t("heatPumpOffersView.new offer for the Heat Pump.") }}
                </span>

                <v-btn
                    variant="flat"
                    size="small"
                    color="primary"
                    :href="`/customers/${customer.id}`"
                >
                    {{ $t("heatPumpOffersView.Return") }}
                </v-btn>
            </v-card-title>

            <div>
                <div v-if="!customer.questionnaire.house_heating_fuel">
                    <p>
                        {{
                            $t("heatPumpOffersView.Form completed incorrectly")
                        }}
                    </p>
                    <inertia-link
                        target="_blank"
                        :href="`/customers/${customer.id}/questionnaire/edit`"
                        class="btn btn-yellow my-1"
                        >{{
                            $t("heatPumpOffersView.Edit the Questionnaire")
                        }}</inertia-link
                    >
                </div>
                <div class="grid grid-cols-3 gap-3 pt-4" v-else>
                    <div class="border p-2 rounded">
                        <h2 class="text-xl font-bold">
                            {{ customer.name }}
                        </h2>
                        <p>{{ customer.address }}</p>
                        <p>{{ customer.phone }}</p>
                        <p>{{ customer.email }}</p>

                        <h2 class="text-xl font-bold pt-3">
                            {{ $t("heatPumpOffersView.Object data") }}
                        </h2>
                        <div>
                            <ul class="pb-2">
                                <li>
                                    {{
                                        $t(
                                            "heatPumpOffersView.Type of fuel used"
                                        )
                                    }}:
                                    {{
                                        customer.questionnaire
                                            .house_heating_fuel.name
                                    }}
                                </li>
                                <li>
                                    {{
                                        $t(
                                            "heatPumpOffersView.The amount of fuel consumed"
                                        )
                                    }}:
                                    {{ customer.questionnaire.fuel_used }}
                                </li>
                                <li>
                                    {{ $t("heatPumpOffersView.Fuel Factor") }}:
                                    {{
                                        customer.questionnaire
                                            .house_heating_fuel.efficiency
                                    }}
                                </li>
                                <li class="font-bold">
                                    {{
                                        $t(
                                            "heatPumpOffersView.Suggested pump power"
                                        )
                                    }}:
                                    {{ fuelSuggestedPumpRequired }}
                                </li>
                            </ul>
                            <hr class="pb-2" />
                            <ul>
                                <li>
                                    {{
                                        $t(
                                            "heatPumpOffersView.Year of construction of the house"
                                        )
                                    }}:
                                    {{ customer.questionnaire.year_built }}
                                </li>
                                <li>
                                    {{ $t("heatPumpOffersView.Usable area") }}:
                                    {{ customer.questionnaire.used_footage }}
                                </li>
                                <li>
                                    {{
                                        $t(
                                            "heatPumpOffersView.Number of inhabitants"
                                        )
                                    }}:
                                    {{
                                        customer.questionnaire.habitants_number
                                    }}
                                </li>
                                <li>
                                    {{ $t("heatPumpOffersView.Class") }}:
                                    {{ houseEnergyClassFactor }}
                                    W/m2
                                </li>
                                <li class="font-bold">
                                    {{
                                        $t(
                                            "heatPumpOffersView.Suggested pump power"
                                        )
                                    }}:
                                    {{ yearSuggestedPumpRequired }}
                                    kW
                                </li>
                            </ul>
                        </div>
                        <div>
                            <questionnaire-sidebar
                                :questionnaire="customer.questionnaire"
                            />
                        </div>
                    </div>
                    <div class="col-span-2 border p-2 rounded">
                        <select-input
                            v-model="selectedHeatpump"
                            disabled
                            class="pb-2 pr-5 w-full mt-3"
                            label=""
                        >
                            <option :value="null" disabled />
                            <option
                                v-for="heatpump in heatpumps"
                                :value="heatpump"
                                :selected="heatpump.id === selectedHeatpump?.id"
                                :class="{
                                    hidden:
                                        heatpump.kw < fuelSuggestedPumpRequired,
                                }"
                            >
                                {{
                                    `${heatpump.brand} ${heatpump.kw} kW ${heatpump.type} `
                                }}
                            </option>
                        </select-input>

                        <select-input
                            v-if="
                                selectedHeatpump &&
                                !selectedHeatpump.circulation_heater
                            "
                            v-model="extras_heater"
                            disabled
                            class="pb-2 pr-5 w-full mt-3"
                            :label="$t('heatPumpOffersView.Add a flow heater?')"
                        >
                            <option :value="false">
                                {{ $t("heatPumpOffersView.No") }}
                            </option>
                            <option :value="true">
                                {{ $t("heatPumpOffersView.Yes") }}
                            </option>
                        </select-input>

                        <select
                            disabled
                            class="pb-2 pr-5 w-full mt-3"
                        >
                            <option v-if="selectedWaterContainer">
                                {{
                                    selectedWaterContainer.formattedName
                                }}
                            </option>
                            <option v-else>
                                Not selected
                            </option>
                        </select>

                        <div class="grid grid-cols-2 gap-3 pt-2">
                            <div v-if="selectedHeatpump">
                                <ul>
                                    <li
                                        @click="
                                            forceCommissionVisibility =
                                                !forceCommissionVisibility
                                        "
                                    >
                                        {{
                                            $t("heatPumpOffersView.Pump type")
                                        }}:
                                        <span class="font-bold"
                                            >{{ selectedHeatpump.type }}
                                        </span>
                                    </li>
                                    <li>
                                        {{
                                            $t(
                                                "heatPumpOffersView.Indoor unit"
                                            )
                                        }}:
                                        <span class="font-bold"
                                            >{{ selectedHeatpump.indoor_unit }}
                                        </span>
                                    </li>
                                    <li>
                                        {{
                                            $t(
                                                "heatPumpOffersView.Outdoor unit"
                                            )
                                        }}:
                                        <span class="font-bold"
                                            >{{ selectedHeatpump.outdoor_unit }}
                                        </span>
                                    </li>
                                    <li>
                                        {{ $t("heatPumpOffersView.Tank") }}:
                                        <span v-if="offer.water_container_id > 0" class="font-bold"
                                            >{{ selectedWaterContainer.formattedName }}
                                        </span>
                                        <span v-else class="font-bold">---</span>
                                    </li>
                                    <li>
                                        {{
                                            $t(
                                                "heatPumpOffersView.Circulation pump"
                                            )
                                        }}:
                                        <span class="font-bold"
                                            >{{
                                                selectedHeatpump.circulation_pump
                                            }}
                                        </span>
                                    </li>
                                    <li>
                                        {{
                                            $t(
                                                "heatPumpOffersView.Three-way valve"
                                            )
                                        }}:
                                        <span class="font-bold"
                                            >{{ selectedHeatpump.valve }}
                                        </span>
                                    </li>
                                    <li>
                                        {{
                                            $t("heatPumpOffersView.Controller")
                                        }}:
                                        <span class="font-bold"
                                            >{{ selectedHeatpump.controller }}
                                        </span>
                                    </li>
                                    <li>
                                        {{
                                            $t(
                                                "heatPumpOffersView.Flow heater"
                                            )
                                        }}:
                                        <span class="font-bold"
                                            >{{ getCirculationHeater }}
                                        </span>
                                    </li>
                                    <li class="my-1">
                                        <input
                                            type="checkbox"
                                            disabled
                                            :checked="extras_tray"
                                            name="extras_tray"
                                            id="extras_tray"
                                            class="mr-1"
                                        /><label for="extras_tray">{{
                                            $t(
                                                "heatPumpOffersView.Add the drip tray"
                                            )
                                        }}</label>
                                    </li>
                                    <li class="my-1">
                                        <input
                                            type="checkbox"
                                            disabled
                                            :checked="extras_titanium_anode"
                                            name="extras_titanium_anode"
                                            id="extras_titanium_anode"
                                            class="mr-1"
                                        /><label for="extras_titanium_anode">{{
                                            $t(
                                                "heatPumpOffersView.Add the ANODE-T300 titanium anode"
                                            )
                                        }}</label>
                                    </li>
                                    <li class="my-1">
                                        <input
                                            type="checkbox"
                                            disabled
                                            :checked="extras_pv_connection"
                                            name="extras_pv_connection"
                                            id="extras_pv_connection"
                                            class="mr-1"
                                        /><label for="extras_pv_connection">{{
                                            $t(
                                                "heatPumpOffersView.Add a connection to solar collectors"
                                            )
                                        }}</label>
                                    </li>
                                    <li class="my-1">
                                        <input
                                            type="checkbox"
                                            disabled
                                            :checked="
                                                extras_other_heat_source_connection
                                            "
                                            name="extras_other_heat_source_connection"
                                            id="extras_other_heat_source_connection"
                                            class="mr-1"
                                        /><label
                                            for="extras_other_heat_source_connection"
                                            >{{
                                                $t(
                                                    "heatPumpOffersView.Keep the old heating system as an alternative (hybrid)"
                                                )
                                            }}</label
                                        >
                                    </li>
                                    <li class="my-1">
                                        <input
                                            type="checkbox"
                                            disabled
                                            :checked="extras_water_buffer"
                                            name="extras_water_buffer"
                                            id="extras_water_buffer"
                                            class="mr-1"
                                        /><label for="extras_water_buffer">{{
                                            $t(
                                                "heatPumpOffersCreate.Add water buffer"
                                            )
                                        }}</label>
                                    </li>
                                    <li class="my-1">
                                        <input
                                            type="checkbox"
                                            disabled
                                            :checked="
                                                extras_subsidy_consultation
                                            "
                                            name="extras_subsidy_consultation"
                                            id="extras_subsidy_consultation"
                                            class="mr-1"
                                        /><label
                                            for="extras_subsidy_consultation"
                                            >{{
                                                $t(
                                                    "heatPumpOffersCreate.Add subsidy consultation"
                                                )
                                            }}</label
                                        >
                                    </li>
                                    <li class="my-1">
                                        <input
                                            type="checkbox"
                                            disabled
                                            :checked="extras_insurance"
                                            name="extras_insurance"
                                            id="extras_insurance"
                                            class="mr-1"
                                        /><label for="extras_insurance">{{
                                            $t(
                                                "heatPumpOffersCreate.Add insurance"
                                            )
                                        }}</label>
                                    </li>
                                    <li class="my-1">
                                        <input
                                            type="checkbox"
                                            disabled
                                            :checked="force_b2b_vat_level"
                                            name="force_b2b_vat_level"
                                            id="force_b2b_vat_level"
                                            class="mr-1"
                                        /><label for="force_b2b_vat_level">{{
                                            $t(
                                                "heatPumpOffersView.Force higher VAT"
                                            )
                                        }}</label>
                                    </li>
                                    <li class="my-1">
                                        <input
                                            type="checkbox"
                                            disabled
                                            :checked="add_transportation_costs"
                                            name="add_transportation_costs"
                                            id="add-transportation-costs"
                                            class="mr-1"
                                        /><label
                                            for="add-transportation-costs"
                                            >{{
                                                $t(
                                                    "heatPumpOffersCreate.Add transportation costs"
                                                )
                                            }}</label
                                        >
                                    </li>
                                </ul>

                                <ul class="mt-3">
                                    <li v-if="add_transportation_costs">
                                        {{
                                            $t(
                                                "heatPumpOffersCreate.Transportation costs"
                                            )
                                        }}:
                                        {{
                                            $numberHelper.formatMoney(
                                                this.offer.transportation_costs
                                            )
                                        }}
                                    </li>
                                    <li
                                        v-if="
                                            godmode || forceCommissionVisibility
                                        "
                                    >
                                        {{
                                            $t(
                                                "heatPumpOffersCreate.The price of add-ons"
                                            )
                                        }}:
                                        {{
                                            $numberHelper.formatMoney(
                                                totalExtrasCost
                                            )
                                        }}
                                    </li>
                                    <li
                                        v-if="
                                            godmode || forceCommissionVisibility
                                        "
                                    >
                                        {{
                                            $t(
                                                "heatPumpOffersCreate.Minimum selling price"
                                            )
                                        }}:
                                        {{
                                            $numberHelper.formatMoney(
                                                baseNetPriceWithExtras
                                            )
                                        }}
                                    </li>
                                    <li
                                        v-if="
                                            godmode || forceCommissionVisibility
                                        "
                                        class="mb-4"
                                    >
                                        {{
                                            $t(
                                                "heatPumpOffersCreate.Advisor's commission"
                                            )
                                        }}:
                                        {{
                                            $numberHelper.formatMoney(
                                                salesCommission
                                            )
                                        }}
                                    </li>
                                    <li
                                        v-if="
                                            extras && extras.subsidies && extras.subsidies.length > 0
                                        "
                                        class="mb-4"
                                    >
                                      <template
                                          v-if="extras.subsidies[0].exists"
                                      >
                                        {{ extras.subsidies[0].title}} : {{
                                          $numberHelper.formatMoney(
                                              extras.subsidies[0].amount
                                          )
                                        }}

                                      </template>
                                      <template
                                          v-else
                                      >{{ $t("heatPumpOffersCreate.No subsidy") }}</template>

                                    </li>
                                    <li
                                        v-if="
                                            extras && extras.financing
                                        "
                                        class="mb-4"
                                    >
                                      <template v-if="extras.financing.available">
                                        {{ extras.financing.title}} : {{
                                          $numberHelper.formatMoney(
                                              extras.financing.monthlyPayment
                                          )
                                        }} / month
                                      </template>
                                      <template
                                          v-else
                                      >{{ $t("heatPumpOffersCreate.No financing") }}</template>

                                    </li>

                                    <li v-if="!splitVatPrices">
                                        {{
                                            $t(
                                                "heatPumpOffersCreate.Net price"
                                            )
                                        }}: {{ finalPrices.net }}
                                    </li>
                                    <li
                                        v-if="!splitVatPrices"
                                        class="font-bold"
                                    >
                                        {{
                                            $t(
                                                "heatPumpOffersCreate.Gross price"
                                            )
                                        }}: {{ finalPrices.gross }}
                                    </li>
                                    <li v-else>
                                        <div
                                            class="relative overflow-x-auto shadow-md sm:rounded-lg"
                                        >
                                            <table
                                                class="w-full text-sm text-left text-gray-500 dark:text-gray-400"
                                            >
                                                <thead
                                                    class="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400"
                                                >
                                                    <tr>
                                                        <th
                                                            scope="col"
                                                            class="p-2"
                                                        >
                                                            {{
                                                                $t(
                                                                    "heatPumpOffersCreate.Net price"
                                                                )
                                                            }}
                                                        </th>
                                                        <th
                                                            scope="col"
                                                            class="p-2"
                                                        >
                                                            {{
                                                                $t(
                                                                    "heatPumpOffersCreate.VAT rate"
                                                                )
                                                            }}
                                                        </th>
                                                        <th
                                                            scope="col"
                                                            class="p-2"
                                                        >
                                                            {{
                                                                $t(
                                                                    "heatPumpOffersCreate.VAT"
                                                                )
                                                            }}
                                                        </th>
                                                        <th
                                                            scope="col"
                                                            class="p-2"
                                                        >
                                                            {{
                                                                $t(
                                                                    "heatPumpOffersCreate.Gross price"
                                                                )
                                                            }}
                                                        </th>
                                                        <th
                                                            scope="col"
                                                            class="p-2"
                                                        >
                                                            {{
                                                                $t(
                                                                    "heatPumpOffersCreate.Area percentage"
                                                                )
                                                            }}
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr
                                                        class="bg-white border-b dark:bg-gray-800 dark:border-gray-700"
                                                    >
                                                        <td class="p-2">
                                                            {{
                                                                splitVatPrices.netPriceOnLowerVat.toFixed(
                                                                    2
                                                                )
                                                            }}
                                                        </td>
                                                        <td class="p-2">8%</td>
                                                        <td class="p-2">
                                                            {{
                                                                (
                                                                    splitVatPrices.netPriceOnLowerVat *
                                                                    0.08
                                                                ).toFixed(2)
                                                            }}
                                                        </td>
                                                        <td class="p-2">
                                                            {{
                                                                splitVatPrices.lowerVatPrice.toFixed(
                                                                    2
                                                                )
                                                            }}
                                                        </td>
                                                        <td class="p-2">
                                                            {{
                                                                (
                                                                    (300 /
                                                                        this
                                                                            .customer
                                                                            .questionnaire
                                                                            .used_footage) *
                                                                    100
                                                                ).toFixed(2)
                                                            }}
                                                            %
                                                        </td>
                                                    </tr>
                                                    <tr
                                                        class="bg-white border-b dark:bg-gray-800 dark:border-gray-700"
                                                    >
                                                        <td class="p-2">
                                                            {{
                                                                splitVatPrices.netPriceOnHigherVat.toFixed(
                                                                    2
                                                                )
                                                            }}
                                                        </td>
                                                        <td class="p-2">23%</td>
                                                        <td class="p-2">
                                                            {{
                                                                (
                                                                    splitVatPrices.netPriceOnHigherVat *
                                                                    0.23
                                                                ).toFixed(2)
                                                            }}
                                                        </td>
                                                        <td class="p-2">
                                                            {{
                                                                splitVatPrices.higherVatPrice.toFixed(
                                                                    2
                                                                )
                                                            }}
                                                        </td>
                                                        <td class="p-2">
                                                            {{
                                                                (
                                                                    (1 -
                                                                        300 /
                                                                            this
                                                                                .customer
                                                                                .questionnaire
                                                                                .used_footage) *
                                                                    100
                                                                ).toFixed(2)
                                                            }}
                                                            %
                                                        </td>
                                                    </tr>
                                                </tbody>
                                                <tfoot>
                                                    <tr>
                                                        <th
                                                            colspan="4"
                                                            class="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400 text-center border py-2"
                                                        >
                                                            {{
                                                                $t(
                                                                    "heatPumpOffersCreate.TOGETHER"
                                                                )
                                                            }}
                                                        </th>
                                                    </tr>
                                                    <tr
                                                        class="font-medium text-gray-900 dark:text-white"
                                                    >
                                                        <td class="p-2">
                                                            {{
                                                                finalPrices.net
                                                            }}
                                                        </td>
                                                        <td class="p-2"></td>
                                                        <td class="p-2">
                                                            {{
                                                                finalPrices.vat
                                                            }}
                                                        </td>
                                                        <td class="p-2">
                                                            {{
                                                                finalPrices.gross
                                                            }}
                                                        </td>
                                                    </tr>
                                                </tfoot>
                                            </table>
                                        </div>
                                    </li>

                                    <li
                                        class="flex my-1"
                                    >
                                        <span class="pt-3"
                                            >{{
                                                $t(
                                                    "heatPumpOffersView.Discount"
                                                )
                                            }}:
                                            <strong>{{
                                                discount
                                            }}</strong></span
                                        >
                                    </li>
                                </ul>
                            </div>
                            <div v-if="selectedHeatpump">
                                <img :src="selectedHeatpump.image_url" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </v-card>
    </authenticated-layout>
</template>

<script>
import AuthenticatedLayout from "@/Layouts/Authenticated";
import EditIcon from "@/Components/Icons/EditIcon";
import SelectInput from "@/Components/Shared/SelectInput";
import BreezeInput from "@/Components/Input";
import find from "lodash/find";
import DownArrowIcon from "@/Components/Icons/DownArrowIcon";
import QuestionnaireSidebar from "@/Components/Partials/QuestionnaireSidebar";

export default {
    props: {
        offer: {
            type: Object,
            required: true,
        },
        heatpumps: {
            type: Array,
            required: true,
        },
        customer: {
            type: Object,
            required: true,
        },
        selectedContainer: {
            type: Object,
            required: false,
            default: () => null,
        },
        houseHeatingFuels: {
            type: Array,
            required: true,
        },
        extras: {
          type: Object,
          required: true,
        },
    },
    components: {
        QuestionnaireSidebar,
        DownArrowIcon,
        EditIcon,
        AuthenticatedLayout,
        SelectInput,
        BreezeInput,
    },
    computed: {
        waterContainerName() {

        },
        userExtraPremium() {
            return parseFloat(this.$page.props.auth.user.extra_premium_hp);
        },
        houseEnergyClassFactor() {
            const yearBuilt = this.customer.questionnaire.year_built;
            if (yearBuilt <= 1982) {
                return 217;
            } else if (yearBuilt <= 1991) {
                return 132;
            } else if (yearBuilt <= 2001) {
                return 107;
            } else if (yearBuilt <= 2008) {
                return 94;
            } else if (yearBuilt <= 2013) {
                return 93;
            } else if (yearBuilt <= 2016) {
                return 56;
            } else if (yearBuilt <= 2020) {
                return 44;
            }
            return 39;
            //todo - passive + zeroenergy
        },
        yearSuggestedPumpRequired() {
            const waterFactor = 350;
            return (
                (this.houseEnergyClassFactor *
                    this.customer.questionnaire.used_footage +
                    this.customer.questionnaire.habitants_number *
                        waterFactor) /
                1000
            );
        },
        fuelSuggestedPumpRequired() {
            if (this.customer.questionnaire.house_heating_fuel) {
                let fuelFactor =
                    this.customer.questionnaire.house_heating_fuel.efficiency;
                return (
                    Math.round(
                        ((fuelFactor * this.customer.questionnaire.fuel_used) /
                            4500) *
                            100
                    ) / 100
                );
            }
            return 0;
        },
        getCirculationHeater() {
            if (!this.selectedHeatpump.circulation_heater) {
                if (this.extras_heater) {
                    return this.$t("heatPumpOffersView.ELK9M 9 kW + assembly");
                } else {
                    return this.$t("heatPumpOffersView.Lack");
                }
            } else {
                return this.selectedHeatpump?.circulation_heater;
            }
        },
        totalExtrasCost() {
            let extras = [
                "extras_heater",
                "extras_tray",
                "extras_titanium_anode",
                "extras_pv_connection",
                "extras_other_heat_source_connection",
                "extras_water_buffer",
                "extras_subsidy_consultation",
                "extras_insurance",
            ];
            let sum = 0;
            for (const extra of extras) {
                if (this.offer[extra]) {
                    console.log("is");
                    sum += this.extrasPricing[extra];
                } else console.log("not");
            }

            let containerPrice = this.selectedContainer ? this.selectedContainer.selling_price : 0;
            return sum + containerPrice;
        },
        baseNetPriceWithExtras() {
            return (
                this.selectedHeatpump?.default_selling_price_net +
                this.totalExtrasCost +
                this.offer.transportation_costs
            );
        },
        finalPriceNet() {
            return (
                this.baseNetPriceWithExtras +
                this.userExtraPremium -
                this.discount
            );
        },
        splitVatPrices() {
            if (this.force_b2b_vat_level) return false;
            if (this.customer.questionnaire.used_footage < 300) return false;

            let percentageOnHigherVat =
                    1 - 300 / this.customer.questionnaire.used_footage,
                netPriceOnHigherVat =
                    this.finalPriceNet * percentageOnHigherVat,
                netPriceOnLowerVat =
                    this.finalPriceNet * (1 - percentageOnHigherVat);

            return {
                percentageOnHigherVat,
                netPriceOnHigherVat,
                netPriceOnLowerVat,
                lowerVatPrice: netPriceOnLowerVat * 1.08,
                higherVatPrice: netPriceOnHigherVat * 1.23,
            };
        },
        finalPrices() {
            let gross, net;
            if (this.force_b2b_vat_level) {
                gross = (this.finalPriceNet * 1.23).toFixed();
                net = gross / 1.23;
            } else {
                if (!this.splitVatPrices) {
                    gross = (this.finalPriceNet * 1.08).toFixed();
                    net = gross / 1.08;
                } else {
                    gross = (
                        this.splitVatPrices.lowerVatPrice +
                        this.splitVatPrices.higherVatPrice
                    ).toFixed();
                    net =
                        this.splitVatPrices.netPriceOnLowerVat +
                        this.splitVatPrices.netPriceOnHigherVat;
                }
            }
            return {
                gross: gross,
                net: net.toFixed(2),
                vat: (gross - net).toFixed(2),
            };
        },
        salesCommission() {
            return (
                this.selectedHeatpump.default_sales_commission +
                (this.finalPriceNet - this.baseNetPriceWithExtras) / 2
            );
        },
    },
    data() {
        return {
            forceCommissionVisibility: false,
            godmode: this.$page.props.auth.user.role !== "sales",
            remember: "form",
            discount: this.offer.discount,
            selectedHeatpump: this.offer.heatpump,
            selectedWaterContainer: this.offer.water_container,
            force_b2b_vat_level: this.offer.force_b2b_vat_level,
            extras_tray: parseFloat(this.offer.extras_tray) > 0,
            extras_heater: parseFloat(this.offer.extras_heater) > 0,
            extras_titanium_anode:
                parseFloat(this.offer.extras_titanium_anode) > 0,
            extras_pv_connection:
                parseFloat(this.offer.extras_pv_connection) > 0,
            extras_other_heat_source_connection:
                parseFloat(this.offer.extras_other_heat_source_connection) > 0,
            extras_subsidy_consultation:
                parseFloat(this.offer.extras_subsidy_consultation) > 0,
            extras_insurance: parseFloat(this.offer.extras_insurance) > 0,
            extras_water_buffer: parseFloat(this.offer.extras_water_buffer) > 0,
            add_transportation_costs: this.offer.transportation_costs > 0,

            extrasPricing: {
                extras_heater: parseFloat(this.offer.extras_heater),
                extras_tray: parseFloat(this.offer.extras_tray),
                extras_titanium_anode: parseFloat(
                    this.offer.extras_titanium_anode
                ),
                extras_pv_connection: parseFloat(
                    this.offer.extras_pv_connection
                ),
                extras_other_heat_source_connection: parseFloat(
                    this.offer.extras_other_heat_source_connection
                ),
                extras_subsidy_consultation: parseFloat(
                    this.offer.extras_subsidy_consultation
                ),
                extras_water_buffer: parseFloat(this.offer.extras_water_buffer),
                extras_insurance: parseFloat(this.offer.extras_insurance),
            },
        };
    },
    mounted() { },
    methods: {
        onFuelChange(e) {
            let fuelId = parseInt(e.target.value);
            let fuel = this.getSelectedFuel(fuelId);
            this.customer.questionnaire.house_heating_fuel.efficiency =
                fuel.efficiency;
            this.customer.questionnaire.house_heating_fuel.name = fuel.name;
            this.customer.questionnaire.house_heating_fuel.id = fuel.id;
        },
        getSelectedFuel(id) {
            return _.find(this.houseHeatingFuels, function (o) {
                return o.id === id;
            });
        },
        getFormData() {
            return {
                customer_id: this.customer.id,
                heat_pump_id: this.selectedHeatpump?.id,
                discount: this.discount,
                force_b2b_vat_level: this.force_b2b_vat_level,
                current_buying_price_net:
                    this.selectedHeatpump?.buying_price_net,
                base_selling_price_net: this.baseNetPriceWithExtras,
                sales_commission: this.salesCommission,
                selling_price_net: this.finalPriceNet,
                extras_tray: this.extras_tray,
                extras_heater: this.extras_heater,
                extras_titanium_anode: this.extras_titanium_anode,
                extras_pv_connection: this.extras_pv_connection,
                extras_other_heat_source_connection:
                    this.extras_other_heat_source_connection,
                extras_subsidy_consultation: this.extras_subsidy_consultation,
                extras_water_buffer: this.extras_water_buffer,
                extras_insurance: this.extras_insurance,
                transportation_costs: this.offer.transportation_costs,
            };
        },
    },
};
</script>
